import { type HTMLAttributes, forwardRef, useState, useRef, useEffect } from 'react'

export interface PopoverProps extends HTMLAttributes<HTMLDivElement> {
  isOpen: boolean
  onClose?: () => void
  title?: string
}

export const Popover = forwardRef<HTMLDivElement, PopoverProps>(
  ({ isOpen, onClose, title, children, className = '', ...props }, _ref) => {
    const [isClosing, setIsClosing] = useState(false)
    const popoverRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
      if (!isOpen) {
        setIsClosing(false)
      }
    }, [isOpen])

    const handleClose = () => {
      setIsClosing(true)
      setTimeout(() => {
        onClose?.()
        setIsClosing(false)
      }, 200)
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        handleClose()
      }
    }

    useEffect(() => {
      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside)
        return () => {
          document.removeEventListener('mousedown', handleClickOutside)
        }
      }
    }, [isOpen])
    if (!isOpen && !isClosing) return null

    return (
      <div
        ref={popoverRef}
        className={`fixed bottom-4 right-4 bg-black/95 rounded-lg shadow-xl transition-opacity duration-200 ${isClosing ? 'opacity-0' : 'opacity-100'} ${className}`}
        style={{ zIndex: 2000, minWidth: '200px' }}
        {...props}
      >
        {title && (
          <div className="flex justify-between items-center px-3 py-2.5 border-b border-white/10">
            <span className="text-white text-sm font-medium">{title}</span>
            {onClose && (
              <button
                className="bg-none border-none text-white/60 text-xl cursor-pointer w-6 h-6 flex items-center justify-center rounded hover:bg-white/10 hover:text-white transition-all"
                onClick={handleClose}
              >
                ×
              </button>
            )}
          </div>
        )}
        <div className="p-3">
          {children}
        </div>
      </div>
    )
  }
)

Popover.displayName = 'Popover'
